// BlogPost.jsx
import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Calendar, User as UserIcon, Clock, ArrowRight, Share2, Facebook, Twitter, Linkedin, Mail, Tag, ChevronRight, Home } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const blogData = {
    "10-essential-tips-for-first-time-home-buyers": {
      title: "10 Essential Tips for First-Time Home Buyers",
      excerpt: "Buying your first home is exciting but can be overwhelming. Discover essential tips to navigate the real estate market confidently and make informed decisions.",
      content: `
        <p>Purchasing your first home is a significant milestone, but it comes with challenges. Here are 10 essential tips to help you navigate the process:</p>
        
        <h2 id="section1">1. Get Pre-Approved for a Mortgage</h2>
        <p>Before you start house hunting, get pre-approved for a mortgage. This gives you a clear budget and shows sellers you're a serious buyer. Shop around with multiple lenders to find the best rates and terms.</p>
        
        <h2 id="section2">2. Research Neighborhoods Thoroughly</h2>
        <p>Visit potential neighborhoods at different times of the day. Consider factors like school districts, commute times, and nearby amenities. Talk to residents and join local Facebook groups to get real insights.</p>
        
        <h2 id="section3">3. Don't Skip the Home Inspection</h2>
        <p>A professional home inspection can reveal hidden issues that might cost you thousands later. Always negotiate repairs or price adjustments based on the inspection report. Consider specialized inspections for pests, mold, or structural issues.</p>
        
        <h2 id="section4">4. Factor in Additional Costs</h2>
        <p>Beyond the purchase price, budget for closing costs (2-5% of home price), property taxes, homeowners insurance, maintenance (1% of home value annually), and potential HOA fees. Don't forget moving costs and new furniture!</p>
        
        <h2 id="section5">5. Work with a Local Real Estate Agent</h2>
        <p>A knowledgeable local agent can provide invaluable insights about market conditions, pricing, and negotiation strategies. They have access to off-market listings and can guide you through the entire process. Interview multiple agents before choosing one.</p>
        
        <h2 id="section6">6. Be Realistic About Your Needs vs. Wants</h2>
        <p>Make a list of must-haves (number of bedrooms, location, budget) versus nice-to-haves (pool, finished basement, gourmet kitchen). This helps you stay focused and avoid getting distracted by features that aren't essential.</p>
        
        <h2 id="section7">7. Check the Property's History</h2>
        <p>Research how long the property has been on the market, price reductions, and any previous sales. Request disclosure documents to learn about past repairs, renovations, or issues. This information can strengthen your negotiation position.</p>
        
        <h2 id="section8">8. Consider Future Resale Value</h2>
        <p>Even if you plan to stay long-term, consider factors that might affect future resale value like location, layout, and neighborhood trends. Look for properties in up-and-coming areas with good growth potential.</p>
        
        <h2 id="section9">9. Get Everything in Writing</h2>
        <p>Ensure all agreements, repairs, and promises are documented in the contract. Verbal agreements are difficult to enforce. Have your real estate attorney review all documents before signing.</p>
        
        <h2 id="section10">10. Trust Your Gut</h2>
        <p>If something feels off about a property or transaction, don't ignore that feeling. It's better to walk away than to regret a hasty decision. There will always be other homes.</p>
      `,
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=630&fit=crop",
      imageAlt: "Happy first-time home buyer holding house keys in front of their new home",
      author: "Sarah Johnson",
      authorBio: "Sarah Johnson is a certified real estate expert with over 12 years of experience helping first-time home buyers navigate the complex real estate market. She has helped over 500 families purchase their dream homes.",
      authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
      date: "2024-03-15",
      updatedDate: "2024-03-15",
      readTime: "8 min read",
      category: "Buying Tips",
      tags: ["first time buyer", "real estate tips", "home buying", "mortgage", "home inspection", "real estate agent"]
    },
    "maximizing-property-value-renovation-roi-guide": {
      title: "Maximizing Your Property's Value: Renovation ROI Guide",
      excerpt: "Not all renovations are created equal. Learn which home improvements offer the best return on investment and how to prioritize your renovation projects.",
      content: `
        <p>Strategic renovations can significantly increase your property's value. Here's what you need to know about maximizing ROI:</p>
        
        <h2 id="section1">Kitchen Remodel (70-80% ROI)</h2>
        <p>The kitchen is often the heart of the home. Focus on mid-range updates like new countertops, cabinet refacing, and energy-efficient appliances rather than luxury overhauls. A complete kitchen renovation can cost $20,000-$50,000 but can add significant value to your home.</p>
        
        <h2 id="section2">Bathroom Updates (60-70% ROI)</h2>
        <p>Modernizing bathrooms delivers strong returns. Consider updating fixtures, adding better lighting, and improving ventilation. Even small changes like new vanities and mirrors make a difference. A mid-range bathroom remodel typically costs $10,000-$15,000.</p>
        
        <h2 id="section3">Curb Appeal Enhancements (70-80% ROI)</h2>
        <p>First impressions matter. Landscaping, fresh exterior paint, new garage doors, and an updated front entrance can dramatically improve your home's appeal and value. Simple landscaping can cost as little as $1,000-$3,000 but offers excellent returns.</p>
        
        <h2 id="section4">Energy Efficiency Upgrades (50-80% ROI)</h2>
        <p>Solar panels, new windows, improved insulation, and smart thermostats attract eco-conscious buyers and reduce utility costs, making your property more attractive. Energy-efficient upgrades often qualify for tax credits and rebates.</p>
        
        <h2 id="section5">Adding Living Space (50-70% ROI)</h2>
        <p>Finished basements, attic conversions, or room additions can add valuable square footage. Focus on creating functional spaces like home offices or additional bedrooms. A basement finish typically costs $15,000-$30,000.</p>
        
        <h2 id="section6">Maintenance Projects (100% ROI+)</h2>
        <p>Don't overlook basic maintenance. New roofing, HVAC systems, or plumbing may not be exciting, but they prevent costly issues and reassure buyers about the property's condition.</p>
        
        <h2 id="section7">Renovations to Approach Carefully</h2>
        <p>Luxury additions like swimming pools, extensive home theaters, or overly personalized designs often have lower ROI. Consider your neighborhood standards and buyer preferences before committing to expensive upgrades.</p>
      `,
      image: "https://static.vecteezy.com/system/resources/previews/027/477/384/non_2x/stock-graph-technology-background-it-is-a-technology-that-shows-the-growth-price-of-a-company-on-the-stock-market-increasing-the-attractiveness-of-investors-green-and-red-charts-along-with-bars-vector.jpg",
      imageAlt: "Modern kitchen renovation showing new countertops and appliances",
      author: "Michael Chen",
      authorBio: "Michael Chen is a real estate investor and renovation expert who has flipped over 100 properties. He specializes in identifying high-ROI renovations and maximizing property values.",
      authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
      date: "2024-03-10",
      updatedDate: "2024-03-10",
      readTime: "6 min read",
      category: "Investment",
      tags: ["home renovation", "ROI", "property value", "remodeling", "kitchen remodel", "bathroom update"]
    },
    "understanding-real-estate-market-cycles": {
      title: "Understanding Real Estate Market Cycles",
      excerpt: "Real estate markets move in cycles. Understanding these patterns can help you make better investment decisions and time your purchases or sales strategically.",
      content: `
        <p>Real estate markets follow predictable cycles. Recognizing where we are in the cycle can inform your investment strategy:</p>
        
        <h2 id="section1">Four Phases of Real Estate Cycles</h2>
        
        <h3 id="section1-1">1. Recovery Phase</h3>
        <p>Following a downturn, the market stabilizes. Vacancy rates decline, rent growth begins, and construction activity remains low. This phase offers opportunities for value investors to purchase properties at below-market prices.</p>
        
        <h3 id="section1-2">2. Expansion Phase</h3>
        <p>Demand increases, rents rise, and new construction begins. Property values appreciate steadily. This is often the best time for developers and growth-focused investors to enter the market.</p>
        
        <h3 id="section1-3">3. Hyper Supply Phase</h3>
        <p>Construction outpaces demand. Vacancy rates start rising, rent growth slows, and property appreciation moderates. Caution is warranted during this phase, and investors should focus on properties with strong fundamentals.</p>
        
        <h3 id="section1-4">4. Recession Phase</h3>
        <p>Supply exceeds demand significantly. Vacancy rates rise, rents may decline, and property values drop. Cash-rich investors can find distressed opportunities and negotiate favorable terms.</p>
        
        <h2 id="section2">Indicators to Watch</h2>
        <p>Pay attention to employment growth, population trends, construction permits, vacancy rates, and days on market to gauge market position. These indicators can help you anticipate market shifts before they happen.</p>
        
        <h2 id="section3">Strategic Implications for Investors</h2>
        <p>In recovery and early expansion, focus on value-add opportunities. In hyper supply, prioritize stable assets with strong fundamentals. During recessions, maintain liquidity and look for distressed sales.</p>
        
        <h2 id="section4">Local Markets Matter Most</h2>
        <p>Real estate is inherently local. While national trends provide context, local economic factors, job markets, and supply constraints drive your specific market conditions. Always research your target market thoroughly.</p>
      `,
      image: "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=1200&h=630&fit=crop",
      imageAlt: "Real estate market trend graph showing cyclical patterns",
      author: "David Williams",
      authorBio: "David Williams is a real estate economist and market analyst with 15 years of experience forecasting market trends. He advises institutional investors on market timing and strategy.",
      authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
      date: "2024-03-05",
      updatedDate: "2024-03-05",
      readTime: "7 min read",
      category: "Market Analysis",
      tags: ["market cycles", "real estate investment", "market analysis", "property trends", "economic indicators"]
    },
    "landlords-guide-property-management-best-practices": {
      title: "Landlord's Guide: Property Management Best Practices",
      excerpt: "Successful property management requires more than just collecting rent. Learn best practices for tenant relations, maintenance, and maximizing your rental income.",
      content: `
        <p>Effective property management maximizes returns and minimizes headaches. Implement these best practices to succeed as a landlord:</p>
        
        <h2 id="section1">Tenant Screening Process</h2>
        <p>Thorough screening prevents future problems. Check credit history (minimum 620-650 score), verify income (typically 3x rent), contact previous landlords, and run background checks. Consistent criteria prevent discrimination claims under Fair Housing laws.</p>
        
        <h2 id="section2">Clear Lease Agreements</h2>
        <p>Comprehensive leases protect both parties. Include rent due dates, late fees, maintenance responsibilities, pet policies, subletting rules, and termination conditions. Review and update leases annually to comply with changing laws.</p>
        
        <h2 id="section3">Proactive Maintenance</h2>
        <p>Regular inspections catch issues early. Create maintenance schedules for HVAC, plumbing, roofing, and appliances. Respond to repair requests promptly (within 24 hours for emergencies) to maintain tenant satisfaction and property condition.</p>
        
        <h2 id="section4">Financial Management</h2>
        <p>Separate personal and rental finances. Maintain reserves for vacancies and emergencies (3-6 months expenses). Track all income and expenses for tax purposes. Consider professional accounting software like Stessa or QuickBooks for rentals.</p>
        
        <h2 id="section5">Legal Compliance</h2>
        <p>Stay updated on landlord-tenant laws, fair housing regulations, security deposit rules, and eviction procedures. Consult local attorneys for jurisdiction-specific requirements. Join local landlord associations for resources and updates.</p>
        
        <h2 id="section6">Tenant Communication</h2>
        <p>Establish clear communication channels. Respond to messages within 24 hours. Conduct periodic check-ins and annual lease reviews. Happy tenants stay longer (reducing turnover costs) and take better care of properties.</p>
        
        <h2 id="section7">When to Hire a Property Manager</h2>
        <p>Consider professional management if you own multiple properties, live far from rentals, lack time for maintenance, or find tenant issues stressful. The 8-12% management fee often proves worthwhile through reduced vacancy and maintenance costs.</p>
      `,
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=630&fit=crop",
      imageAlt: "Property manager meeting with tenants in a rental property",
      author: "Jennifer Martinez",
      authorBio: "Jennifer Martinez is a professional property manager and real estate attorney who manages over 500 rental units. She specializes in landlord-tenant law and property management best practices.",
      authorImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop",
      date: "2024-02-28",
      updatedDate: "2024-02-28",
      readTime: "9 min read",
      category: "Property Management",
      tags: ["property management", "landlord tips", "tenant screening", "rental property", "maintenance", "legal compliance"]
    }
  };

  const blog = blogData[slug];

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!blog) {
    return (
      <>
        <Helmet>
          <title>404 - Blog Post Not Found | EstateGhar Real Estate</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href="https://www.estateghar.com/blogposts/{slug}" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center px-4">
            <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
            <p className="text-xl text-gray-600 mb-6">Oops! This blog post doesn't exist.</p>
            <Link to="/blogs" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to all blogs
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Generate table of contents from content
  const extractHeadings = () => {
    const headingMatches = blog.content.match(/<h2[^>]*id="([^"]*)"[^>]*>([^<]*)<\/h2>/g) || [];
    return headingMatches.map(match => {
      const idMatch = match.match(/id="([^"]*)"/);
      const textMatch = match.match(/<h2[^>]*>([^<]*)<\/h2>/);
      return {
        id: idMatch ? idMatch[1] : '',
        text: textMatch ? textMatch[1] : ''
      };
    });
  };

  const headings = extractHeadings();

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <Helmet>
        <title>{`${blog.title} | EstateGhar Real Estate Blog`}</title>
        <meta name="description" content={blog.excerpt} />
        <meta name="keywords" content={blog.tags.join(", ")} />
        <meta name="author" content={blog.author} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://estateghar.com/blog/${slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://estateghar.com/blog/${slug}`} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:image:alt" content={blog.imageAlt} />
        <meta property="og:site_name" content="EstateGhar Real Estate" />
        
        {/* Article specific OG tags */}
        <meta property="article:published_time" content={blog.date} />
        <meta property="article:modified_time" content={blog.updatedDate} />
        <meta property="article:author" content={blog.author} />
        <meta property="article:section" content={blog.category} />
        {blog.tags.map((tag, index) => (
          <meta property="article:tag" content={tag} key={index} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.excerpt} />
        <meta name="twitter:image" content={blog.image} />
        <meta name="twitter:image:alt" content={blog.imageAlt} />
        <meta name="twitter:creator" content="@EstateGhar" />
        
        {/* Additional meta tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#059669" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <article className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm mb-6 text-gray-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-green-600 flex items-center gap-1">
              <Home className="w-4 h-4" />
              Home
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blogs" className="hover:text-green-600">Blogs</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium line-clamp-1">{blog.title}</span>
          </nav>

          {/* Hero Image */}
          <img
            src={blog.image}
            alt={blog.imageAlt}
            className="w-full h-64 sm:h-96 object-cover rounded-xl shadow-lg mb-8"
            loading="eager"
          />

          {/* Category Badge */}
          <div className="flex items-center gap-2 mb-4">
            <Link 
              to={`/blogs?category=${encodeURIComponent(blog.category)}`}
              className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
            >
              {blog.category}
            </Link>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {blog.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-2 text-gray-600">
              <UserIcon className="w-4 h-4" />
              <span>{blog.author}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4" />
              <time dateTime={blog.date}>Published: {new Date(blog.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4" />
              <span>{blog.readTime}</span>
            </div>
          </div>

          {/* Table of Contents */}
          {headings.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Table of Contents</h3>
              <ul className="space-y-2">
                {headings.map((heading, index) => (
                  <li key={index}>
                    <a 
                      href={`#${heading.id}`}
                      className="text-green-600 hover:text-green-700 hover:underline flex items-center gap-2"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Main Content */}
          <div 
            className="prose prose-lg prose-green max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Tags Section */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <Tag className="w-5 h-5 text-gray-500" />
              {blog.tags.map(tag => (
                <Link
                  key={tag}
                  to={`/blogs?search=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          {/* Author Bio Section */}
          <div className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 flex flex-col sm:flex-row gap-6 items-start">
            <img
              src={blog.authorImage}
              alt={blog.author}
              className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md"
            />
            <div>
              <h4 className="font-bold text-xl text-gray-900 mb-1">{blog.author}</h4>
              <p className="text-gray-600 text-sm mb-3">{blog.authorBio}</p>
              <div className="flex gap-3">
                <button className="text-gray-500 hover:text-green-600 transition-colors">
                  <Facebook className="w-5 h-5" />
                </button>
                <button className="text-gray-500 hover:text-green-600 transition-colors">
                  <Twitter className="w-5 h-5" />
                </button>
                <button className="text-gray-500 hover:text-green-600 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Social Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share this article
            </h3>
            <div className="flex gap-3 flex-wrap">
              <button 
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`)}
                className="px-4 py-2 bg-[#1877f2] text-white rounded-lg hover:bg-[#166fe5] transition-colors flex items-center gap-2"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </button>
              <button 
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(currentUrl)}`)}
                className="px-4 py-2 bg-[#1da1f2] text-white rounded-lg hover:bg-[#1a91da] transition-colors flex items-center gap-2"
              >
                <Twitter className="w-4 h-4" />
                Twitter
              </button>
              <button 
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`)}
                className="px-4 py-2 bg-[#0a66c2] text-white rounded-lg hover:bg-[#0955a8] transition-colors flex items-center gap-2"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </button>
              <button 
                onClick={() => window.location.href = `mailto:?subject=${encodeURIComponent(blog.title)}&body=${encodeURIComponent(currentUrl)}`}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="mt-12 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-3">Never Miss Real Estate Insights</h3>
            <p className="text-green-50 mb-6 max-w-md mx-auto">
              Subscribe to get the latest tips, market updates, and investment strategies
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" action="/subscribe" method="POST">
              <input
                type="email"
                name="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                required
                aria-label="Email address for newsletter"
              />
              <button type="submit" className="px-6 py-2 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors">
                Subscribe Now
              </button>
            </form>
            <p className="text-xs text-green-200 mt-4">No spam. Unsubscribe anytime.</p>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost;